import { useEffect, useRef, useState } from "react";
import type { ApiKline } from "../services/api";
import { fmtNum, fmtPercent, fmtVolume } from "../services/format";

export interface ChartMarker {
  index: number;
  text: string;
  position: "up" | "down";
  kind?: "signal" | "count" | "signal9";
}

interface Props {
  klines: ApiKline[];
  markers?: ChartMarker[];
  height?: number;
}

// 左侧价位轴宽度：K线与价位标签分离
const PLOT_LEFT = 46;

// K线图：日期从左到右，支持 D/K 信号标记与神奇9转数字标记。
// 蜡烛获得鼠标/触摸焦点时放大并弹出浮动信息框，失去焦点时关闭。
export function KlineChart({ klines, markers = [], height = 150 }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  // hovered：鼠标悬停；selected：点击/触摸固定
  const [hovered, setHovered] = useState<number | null>(null);
  const [selected, setSelected] = useState<number | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      setWidth(entries[0]!.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (klines.length === 0) {
    return (
      <div
        className="flat"
        style={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        暂无数据
      </div>
    );
  }

  const n = klines.length;
  const paddingTop = 26;
  const paddingBottom = 24;
  const plotHeight = height - paddingTop - paddingBottom;
  const plotWidth = Math.max(0, width - PLOT_LEFT);

  let minLow = Infinity;
  let maxHigh = -Infinity;
  let maxHighIdx = 0;
  let minLowIdx = 0;
  for (let i = 0; i < klines.length; i++) {
    if (klines[i]!.high > maxHigh) {
      maxHigh = klines[i]!.high;
      maxHighIdx = i;
    }
    if (klines[i]!.low < minLow) {
      minLow = klines[i]!.low;
      minLowIdx = i;
    }
  }
  const range = maxHigh - minLow || 1;
  const minY = minLow - range * 0.05;
  const maxY = maxHigh + range * 0.05;

  const y = (v: number) => paddingTop + ((maxY - v) / (maxY - minY)) * plotHeight;
  const candleStep = plotWidth / n;
  const bodyWBase = Math.min(12, Math.max(1.5, candleStep * 0.6));
  const xCenter = (i: number) => PLOT_LEFT + i * candleStep + candleStep / 2;

  // 当前激活的蜡烛：点击固定优先于悬停
  const active = selected ?? hovered;
  const activeIn = (i: number) => active === i;

  // 颜色全部使用 CSS 变量内联样式，主题切换时即时联动，无需重渲染
  const upVar = "var(--up)";
  const downVar = "var(--down)";
  const accentVar = "var(--accent)";
  const gridVar = "var(--chart-grid)";
  const fg2Var = "var(--fg-2)";

  const prevClose = (i: number): number | null => (i > 0 ? klines[i - 1]!.close : null);

  // 浮动框智能定位：蜡烛靠右时框在左侧，靠左时框在右侧
  const tooltipW = 148;
  const sel = active != null ? klines[active] : null;
  const tooltipLeft =
    active != null
      ? Math.min(
          Math.max(xCenter(active) + (active > n / 2 ? -tooltipW - 10 : 10), PLOT_LEFT + 4),
          width - tooltipW - 4,
        )
      : 0;

  const markerColor = (m: ChartMarker) => {
    if (m.kind === "count") return m.position === "up" ? upVar : downVar;
    return accentVar;
  };

  return (
    // 点击/触摸空白区域关闭浮动窗（蜡烛上的事件已 stopPropagation）
    <div
      ref={containerRef}
      className="kline-chart"
      style={{ position: "relative", width: "100%" }}
      onMouseDown={() => setSelected(null)}
      onTouchStart={() => setSelected(null)}
    >
      {width > 0 && (
        <svg width={width} height={height} style={{ display: "block", userSelect: "none" }}>
          {/* 网格线 + 左侧价位标签（含最高、最低两条边界线） */}
          {[0, 0.2, 0.4, 0.6, 0.8, 1].map((r) => {
            // 价位线对齐真实数据范围：顶部 = 最高价，底部 = 最低价
            const price = maxHigh - (maxHigh - minLow) * r;
            const gy = y(price);
            return (
              <g key={r}>
                <line
                  x1={PLOT_LEFT}
                  x2={width}
                  y1={gy}
                  y2={gy}
                  strokeWidth={0.5}
                  style={{ stroke: gridVar }}
                />
                <text x={4} y={gy - 3} fontSize={9} style={{ fill: fg2Var }}>
                  {fmtNum(price)}
                </text>
              </g>
            );
          })}

          {/* 蜡烛 */}
          {klines.map((k, i) => {
            const cx = xCenter(i);
            const isUp = k.close >= k.open;
            const color = isUp ? upVar : downVar;
            const focused = activeIn(i);
            const bodyW = focused ? Math.min(16, bodyWBase * 1.7) : bodyWBase;
            const wickW = focused ? 1.6 : 1;
            return (
              <g
                key={i}
                style={{ cursor: "pointer" }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered((h) => (h === i ? null : h))}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setSelected(i);
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  setSelected(i);
                }}
              >
                {/* 影线 */}
                <line
                  x1={cx}
                  x2={cx}
                  y1={y(k.high)}
                  y2={y(k.low)}
                  strokeWidth={wickW}
                  style={{ stroke: color }}
                />
                {/* 实体（聚焦时放大） */}
                <rect
                  x={cx - bodyW / 2}
                  y={Math.min(y(k.open), y(k.close))}
                  width={bodyW}
                  height={Math.max(1, Math.abs(y(k.close) - y(k.open)))}
                  style={{ fill: color, stroke: focused ? accentVar : "none", strokeWidth: focused ? 1 : 0 }}
                />
              </g>
            );
          })}

          {/* 标记：D/K 信号、神奇9转数字、9转信号日期标注 */}
          {markers.map((m, idx) => {
            const cx = xCenter(m.index);

            // 神奇9转信号标注：单个箭头符号，不显示日期以免过宽。
            // 上涨9转（顶部风险）箭头朝下 ▼ 预示下跌；下跌9转（底部机会）箭头朝上 ▲ 预示上涨。
            if (m.kind === "signal9") {
              const isUp = m.position === "up";
              const symbol = isUp ? "▼" : "▲";
              // 与蜡烛、神奇数字垂直对齐堆叠：
              // 上方：蜡烛 → 数字(high-4) → 箭头(high-20)；下方：蜡烛 → 数字(low+14) → 箭头(low+28)
              // 极端位置（最高/最低蜡烛）时向内侧收拢避免裁切
              const labelY = isUp
                ? Math.max(y(klines[m.index]!.high) - 20, 8)
                : Math.min(y(klines[m.index]!.low) + 28, height - 5);
              // 单符号很窄，仅最右/最左时轻微收拢避免裁切
              let anchor: "middle" | "start" | "end" = "middle";
              let lx = cx;
              if (cx + 7 > width - 3) {
                anchor = "end";
                lx = width - 3;
              } else if (cx - 7 < PLOT_LEFT + 3) {
                anchor = "start";
                lx = PLOT_LEFT + 3;
              }
              return (
                <text
                  key={idx}
                  x={lx}
                  y={labelY}
                  fontSize={12}
                  fontWeight={700}
                  textAnchor={anchor}
                  pointerEvents="none"
                  style={{ fill: accentVar }}
                >
                  {symbol}
                </text>
              );
            }

            const color = markerColor(m);
            // 神奇9转数字：圆形徽章展示，与价格数字区分，避免混淆
            if (m.kind === "count") {
              const cy =
                m.position === "up"
                  ? y(klines[m.index]!.high) - 9
                  : y(klines[m.index]!.low) + 12;
              return (
                <g key={idx} pointerEvents="none">
                  <circle cx={cx} cy={cy} r={5.5} style={{ fill: color, opacity: 0.9 }} />
                  <text
                    x={cx}
                    y={cy + 3.5}
                    fontSize={8}
                    fontWeight={700}
                    textAnchor="middle"
                    fill="#fff"
                  >
                    {m.text}
                  </text>
                </g>
              );
            }
            const yPos =
              m.position === "up"
                ? y(klines[m.index]!.high) - 2
                : y(klines[m.index]!.low) + 12;
            return (
              <text
                key={idx}
                x={cx}
                y={yPos}
                fontSize={11}
                fontWeight={700}
                textAnchor="middle"
                pointerEvents="none"
                style={{ fill: color }}
              >
                {m.text}
              </text>
            );
          })}

          {/* 最高价 / 最低价：斜线指向蜡烛影线并标注价格，侧别与9转数字相反 */}
          {[
            {
              idx: maxHighIdx,
              price: fmtNum(maxHigh),
              fill: upVar,
              // 蜡烛上方有9转数字/箭头时，标注换到下方
              flip: markers.some((m) => m.index === maxHighIdx && m.position === "up"),
              isMax: true,
            },
            {
              idx: minLowIdx,
              price: fmtNum(minLow),
              fill: downVar,
              // 蜡烛下方有9转数字/箭头时，标注换到上方
              flip: markers.some((m) => m.index === minLowIdx && m.position === "down"),
              isMax: false,
            },
          ].map((a) => {
            const cx = xCenter(a.idx);
            const k = klines[a.idx]!;
            const labelW = a.price.length * 5.4 + 6;
            // 标签水平夹紧：默认放蜡烛右侧，放不下换左侧，再不行贴右缘
            let anchor: "start" | "end" = "start";
            let lx = cx + 14;
            if (lx + labelW > width - 3) {
              anchor = "end";
              lx = cx - 14;
              if (lx - labelW < PLOT_LEFT + 3) {
                anchor = "end";
                lx = width - 3;
              }
            }
            // 标注侧：最高默认上方、最低默认下方；该侧有9转数字时取反
            const above = a.isMax ? !a.flip : a.flip;
            // 指向点：蜡烛影线顶端/底端
            const apexY = above ? y(k.high) : y(k.low);
            // 价格标签：位于指向点外侧上方/下方
            const labelY = above ? Math.max(apexY - 14, 8) : Math.min(apexY + 14, height - 5);
            // 斜线：从标签中下方连到影线指向点
            const labelCenterX = anchor === "start" ? lx + labelW / 2 : lx - labelW / 2;
            return (
              <g key={`hl-${a.idx}`} pointerEvents="none">
                <line
                  x1={labelCenterX}
                  y1={labelY}
                  x2={cx}
                  y2={apexY}
                  strokeWidth={0.75}
                  style={{ stroke: a.fill, opacity: 0.6 }}
                />
                <text
                  x={lx}
                  y={labelY}
                  fontSize={9}
                  fontWeight={700}
                  textAnchor={anchor}
                  style={{ fill: a.fill }}
                >
                  {a.price}
                </text>
              </g>
            );
          })}
        </svg>
      )}

      {/* 浮动信息框 */}
      {sel && active != null && (
        <div
          style={{
            position: "absolute",
            left: tooltipLeft,
            top: 4,
            width: tooltipW,
            background: "var(--chart-tooltip)",
            color: "var(--chart-tooltip-fg)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: 11,
            lineHeight: 1.7,
            pointerEvents: "none",
            zIndex: 10,
            boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
          }}
        >
          <div style={{ fontWeight: 600 }}>{sel.date}</div>
          <div className="flex justify-between">
            <span>收盘</span>
            <span>{fmtNum(sel.close)}</span>
          </div>
          <div className="flex justify-between">
            <span>开盘</span>
            <span>{fmtNum(sel.open)}</span>
          </div>
          <div className="flex justify-between">
            <span>最高</span>
            <span>{fmtNum(sel.high)}</span>
          </div>
          <div className="flex justify-between">
            <span>最低</span>
            <span>{fmtNum(sel.low)}</span>
          </div>
          
          <div className="flex justify-between">
            <span>交易量</span>
            <span>{fmtVolume(sel.volume)}</span>
          </div>
          <div className="flex justify-between">
            <span>涨跌幅</span>
            <span
              className={
                (() => {
                  const p = prevClose(active);
                  if (p == null) return "flat";
                  const pc = ((sel.close - p) / p) * 100;
                  return pc > 0 ? "up" : pc < 0 ? "down" : "flat";
                })()
              }
            >
              {prevClose(active) != null
                ? fmtPercent((sel.close - prevClose(active)!) / prevClose(active)!)
                : "--"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
